import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import axios from "axios";
import AnimatedSection from "../components/AnimatedSection";
import LoadingScreen from "../components/LoadingScreen";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "";

const ProductDetails = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState("");

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);

      try {
        const res = await axios.get(`${API_BASE}/api/products/${id}`);
        setProduct(res.data);
        setMainImage(res.data.coverImage || "");
      } catch (err) {
        console.error(err);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProduct();
  }, [id]);

  // ✅ GLOBAL LOADING STATE
  if (loading) {
    return <LoadingScreen text="Loading product..." />;
  }

  if (!product) {
    return (
      <div className="flex justify-center items-center h-[70vh] text-red-500 text-lg">
        Product not found
      </div>
    );
  }

  return (
    <div className="bg-slate-100 min-h-screen pt-5 lg:pt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-16 space-y-12">
        {/* Back Link */}
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-green-600 transition-colors font-medium"
        >
          <ArrowLeft size={18} /> Back to Products
        </Link>

        {/* Product Grid */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* LEFT → Images */}
          <AnimatedSection className="flex-1">
            <div className="flex flex-col gap-4 lg:gap-6 pb-6 lg:pb-12">
              {/* Main Image */}
              <div className="w-full rounded-xl overflow-hidden lg:rounded-2xl shadow-sm lg:shadow-lg transition-all">
                <img
                  src={mainImage}
                  alt={product.name}
                  className="w-full h-[42vh] sm:h-[55vh] lg:h-[70vh] object-cover"
                />
              </div>

              {/* Thumbnails */}
              <div className="flex gap-3 lg:gap-4 overflow-x-auto py-1 lg:py-2">
                {[product.coverImage, ...(product.otherImages || [])].map(
                  (img, i) => (
                    <button
                      key={i}
                      onClick={() => setMainImage(img)}
                      className={`flex-shrink-0 rounded-lg lg:rounded-xl overflow-hidden border transition ${
                        mainImage === img
                          ? "border-green-600 ring-1 lg:ring-2 ring-green-100"
                          : "border-gray-300"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Thumbnail ${i + 1}`}
                        className="w-20 h-20 lg:w-24 lg:h-24 object-cover"
                      />
                    </button>
                  ),
                )}
              </div>

              {/* WhatsApp Button */}
              {WHATSAPP_NUMBER && (
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hello, I'm interested in ${encodeURIComponent(
                    product.name,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full lg:w-2/3 mx-auto flex items-center justify-center gap-2 lg:gap-3 bg-green-600 lg:bg-gradient-to-r lg:from-green-600 lg:to-green-700 text-white font-semibold px-4 py-3 rounded-xl lg:rounded-2xl shadow-md lg:shadow-lg hover:bg-green-700 transition"
                >
                  <FaWhatsapp size={22} className="lg:w-7 lg:h-7" />
                  Inquire via WhatsApp
                </a>
              )}
            </div>
          </AnimatedSection>

          {/* RIGHT → Info */}
          <AnimatedSection delay={0.1} className="flex-1">
            <span className="text-sm font-medium text-green-600 uppercase tracking-wide">
              {product.category}
            </span>

            <h1 className="mt-2 mb-4 text-3xl sm:text-4xl md:text-5xl font-semibold text-gray-900 leading-tight">
              {product.name}
            </h1>

            {/* Description */}
            <div className="mb-8 space-y-4 text-gray-600">
              {product.description.split("\n").map((line, idx) => (
                <p key={idx} className="text-base md:text-lg leading-relaxed">
                  {line}
                </p>
              ))}
            </div>

            {/* Specifications */}
            {product.specifications?.length > 0 && (
              <div className="mb-8">
                <h3 className="font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  Specifications <ExternalLink size={18} />
                </h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  {product.specifications.map((s, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-xl p-4 shadow hover:shadow-lg transition"
                    >
                      <span className="text-xs text-gray-500 uppercase tracking-wide">
                        {s.feature || s.label}
                      </span>
                      <p className="font-medium text-gray-900 mt-1">
                        {s.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </AnimatedSection>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
